import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';

import { authApi, ApiError } from '../lib/api.js';
import { useAuthStore } from '../lib/stores.js';

// Target of the {verify_url} link Django emails to new users. The token
// arrives in the query string. POSTing it to /api/auth/email/verify both
// confirms the address and signs the user in — the API returns a session
// so we drop the user straight into the app instead of asking for the
// password again.
export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [status, setStatus] = useState('verifying'); // verifying | ok | error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setErrorMessage('No verification token in URL.');
      setStatus('error');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await authApi.confirmEmailVerify(token);
        if (cancelled) return;
        setSession({
          token: res.session.token,
          user: res.user,
          profiles: res.profiles,
        });
        setStatus('ok');
        // Brief pause so the user sees "Email verified" before the route swap.
        setTimeout(() => navigate('/', { replace: true }), 800);
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(err instanceof ApiError ? err.message : 'Verification failed.');
        setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [params, setSession, navigate]);

  return (
    <div style={{ maxWidth: 480, margin: '12vh auto', padding: '0 24px', textAlign: 'center' }}>
      <h1>Email verification</h1>
      {status === 'verifying' && <p className="muted">Verifying your email address…</p>}
      {status === 'ok' && (
        <>
          <p>Email verified — signing you in.</p>
          <p className="muted">Redirecting to the app.</p>
        </>
      )}
      {status === 'error' && (
        <>
          <p>{errorMessage || 'This verification link is invalid or has expired.'}</p>
          <p className="muted">
            <Link to="/">Back to the app</Link> — sign in to request a new verification email.
          </p>
        </>
      )}
    </div>
  );
}
